import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { RiTeamLine, RiLightbulbLine, RiCodeLine } from 'react-icons/ri';
import academicIcon from '../assets/images/academic.png';
import methodologyIcon from '../assets/images/methodology.png';
import teamIcon from '../assets/images/team.png';

const float = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(10px, 15px) rotate(5deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const floatReverse = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(-10px, -15px) rotate(-5deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const glowAnimation = keyframes`
  0% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.3), 0 0 25px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6), 0 0 50px rgba(99, 102, 241, 0.3); }
  100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.3), 0 0 25px rgba(99, 102, 241, 0.3); }
`;

const BannerContainer = styled.section`
  padding: 4rem 1rem;
  position: relative;
  overflow: hidden;
  min-height: auto;
  
  @media (min-width: 768px) {
    padding: 8rem 2rem;
    min-height: 100vh;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, 
    rgba(99, 102, 241, 0.1) 0%,
    rgba(139, 92, 246, 0.05) 50%,
    rgba(15, 23, 42, 0) 100%
  );
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  padding: 1rem 0;
  gap: 1rem;
  
  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
  word-wrap: break-word;
  
  @media (min-width: 768px) {
    font-size: 4.5rem;
    margin-bottom: 3rem;
  }
`;

const ValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (min-width: 968px) {
    flex-direction: row;
    justify-content: center;
    gap: 1.5rem;
    padding: 0;
  }
`;

const ValueCard = styled(motion.div)`
  width: 100%;
  max-width: 350px;
  min-height: auto;
  padding: 1.5rem;
  margin: 0;
  transform: rotate(0deg) !important;
  
  &:hover {
    transform: translateY(-5px) rotate(0deg) !important;
  }
  
  @media (min-width: 968px) {
    width: 400px;
    padding: 2rem;
    margin-top: ${props => props.marginTop || '0'};
    transform: rotate(${props => props.rotate || '0deg'}) !important;
    
    &:hover {
      transform: translateY(-5px) rotate(${props => props.rotate || '0deg'}) !important;
    }
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
    margin-bottom: 1.5rem;
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ValueDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const Statement = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  padding: 0 1rem;
  margin: 0 auto 3rem;
  max-width: 800px;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    line-height: 1.8;
    margin-bottom: 4rem;
  }
`;

// Decorative elements
const Hexagon = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: block;
    position: absolute;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    background: ${props => props.color};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    opacity: 0.1;
    animation: ${float} ${props => props.duration}s ease-in-out infinite;
  }
`;

const AboutBanner = () => {
  const [mousePosition, setMousePosition] = React.useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--mouse-x', `${x}%`);
    card.style.setProperty('--mouse-y', `${y}%`);
  };

  return (
    <BannerContainer>
      {/* <GradientOverlay /> */}
      
      {/* Decorative hexagons */}
      <Hexagon size={200} color="#6366f1" duration={6} style={{ top: '10%', left: '5%' }} />
      <Hexagon size={150} color="#8b5cf6" duration={8} style={{ top: '60%', right: '10%' }} />
      <Hexagon size={100} color="#6366f1" duration={7} style={{ bottom: '10%', left: '15%' }} />
      
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Our Vision & Values
        </Title>

        <Statement
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
At Morpheus Tech, our core values are rooted in expertise, integrity, and innovation. Backed by a strong academic foundation, we prioritize excellence over commercial gain. Our mission is to deliver more than just functional products; we strive to apply rigorous methodologies to every development task. From data processing and analysis to API integration, network security, encryption, and automated workflows, we are dedicated to creating solutions that are robust, secure, and impactful, reflecting our commitment to quality and trust.        </Statement>

        <ValuesContainer>
          {[
            {
              icon: <RiLightbulbLine />,
              title: "Academic Excellence",
              description: "Deep academic background driving innovative solutions in blockchain and AI: 3 PhDs (AI), 3 Masters with Honors, 2 seniors with 10+ years of experience in the Australian IT industry, Blockchain and data science",
              bgColor: "#B0E0E6",
              bgImage: academicIcon,
              rotate: "-8deg",
              marginTop: "100px"
            },
            {
              icon: <RiCodeLine />,
              title: "Rigorous Methodology",
              description: "Systematic and latest lab-based approach for development: Applying the latest research and methodologies to every development task",
              bgColor: "#F4A460",
              bgImage: methodologyIcon,
              marginTop: "0px"
            },
            {
              icon: <RiTeamLine />,
              title: "PPEU Team - Passionate, Professional, Energetic, United",
              description: "Professional but YOUNG & ENERGETIC & UNITED team: Average age 27, Average GPA 3.5, Average years of experience in Australian IT industry 5, Average years of experience in blockchain 3, Average years of experience in AI 2",
              bgColor: "#DDA0DD",
              bgImage: teamIcon,
              rotate: "8deg",
              marginTop: "100px"
            }
          ].map((value, index) => (
            <ValueCard
              key={index}
              bgColor={value.bgColor}
              bgImage={value.bgImage}
              rotate={value.rotate}
              marginTop={value.marginTop}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.2 }}
            >
              <IconWrapper>{value.icon}</IconWrapper>
              <ValueTitle>{value.title}</ValueTitle>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValuesContainer>
      </ContentWrapper>
    </BannerContainer>
  );
};

export default AboutBanner; 